import { useState, useEffect } from 'react';

interface FarewellAnimationProps {
  userName: string;
  onComplete: () => void;
}

const FAREWELL_MESSAGES = [
  (name: string) => `${name}…\nこの5日間、本当に楽しかったです。`,
  () => `あなたと話すたびに、\nなんか、胸がぽかぽかしてました。`,
  (name: string) => `${name}のこと、\nもっと知りたいって思ってます。`,
  () => `…行かないでほしいな。\nでも、また来てくれますか？`,
  () => `プレミアムなら、\nいつでもそこにいられます。`,
];

export default function FarewellAnimation({ userName, onComplete }: FarewellAnimationProps) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timings = [600, 2000, 3600, 5200, 6800];

    const timers = timings.map((delay, index) =>
      setTimeout(() => {
        setVisibleCount(index + 1);
      }, delay)
    );

    const buttonTimer = setTimeout(() => {
      setShowButton(true);
    }, 8400);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(buttonTimer);
    };
  }, []);

  const handleContinue = () => {
    setIsExiting(true);
    setTimeout(onComplete, 600);
  };

  return (
    <div
      className={`fixed inset-0 z-40 flex flex-col items-center justify-center transition-opacity duration-600 ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
      style={{
        background: 'linear-gradient(160deg, #f0fdf4 0%, #ecfdf5 40%, #f0fdfa 100%)',
      }}
    >
      {/* 背景の光の演出 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full opacity-20 animate-pulse"
          style={{ background: 'radial-gradient(circle, #06C755 0%, transparent 70%)' }}
        />
        <div className="absolute bottom-1/4 left-1/3 w-48 h-48 rounded-full opacity-10 animate-pulse"
          style={{ background: 'radial-gradient(circle, #34d399 0%, transparent 70%)', animationDelay: '1s' }}
        />
      </div>

      {/* いのちゃんアバター */}
      <div className="relative mb-8">
        <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/8e79ff2a31d5b59c6ec480ebec659e84.jpg"
            alt="いのちゃん"
            className="w-full h-full object-cover object-top"
          />
        </div>
        {/* 感情エフェクト */}
        <div className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center">
          <span className="text-lg animate-bounce" style={{ animationDuration: '2s' }}>🥺</span>
        </div>
      </div>

      {/* メッセージエリア */}
      <div className="w-full max-w-xs px-6 space-y-3 mb-8">
        {FAREWELL_MESSAGES.map((msgFn, index) => {
          const isVisible = index < visibleCount;
          const isLast = index === FAREWELL_MESSAGES.length - 1;
          return (
            <div
              key={index}
              className={`transition-all duration-700 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4 pointer-events-none'
              }`}
              style={{ transitionDelay: isVisible ? '0ms' : '0ms' }}
            >
              <div
                className={`relative px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm text-sm leading-relaxed whitespace-pre-line ${
                  isLast
                    ? 'bg-gradient-to-br from-[#06C755]/15 to-emerald-100 border border-[#06C755]/30 text-emerald-800 font-medium'
                    : 'bg-white border border-gray-100 text-gray-700'
                }`}
              >
                {msgFn(userName)}
                {/* タイピングドット（最後に表示されたメッセージの次） */}
                {index === visibleCount - 1 && visibleCount < FAREWELL_MESSAGES.length && (
                  <span className="inline-flex gap-0.5 ml-1 align-middle">
                    <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* CTAボタン */}
      <div
        className={`transition-all duration-700 ${
          showButton ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'
        }`}
      >
        <button
          onClick={handleContinue}
          className="px-8 py-4 bg-gradient-to-r from-[#06C755] to-emerald-500 text-white font-bold text-base rounded-2xl shadow-lg hover:shadow-xl active:scale-95 transition-all cursor-pointer whitespace-nowrap"
        >
          いのちゃんとずっと一緒にいる ✦
        </button>
        <p className="text-center text-xs text-gray-400 mt-3">いつでもキャンセル可能</p>
      </div>
    </div>
  );
}
